import { CircleXIcon, SearchIcon } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Input, type InputProps } from '@src/design-system/input';
import { useDebounceCallback } from '@shared/hooks/use-debounce-args';
import { cn } from '../helpers';

type SearchInputProps = InputProps & {
  debouncedTime?: number;
  onRemoveValue?: () => void;
  queryKey?: string;
  paramsToExcludeFromUrl?: string[];
  onValueChange?: (value?: string) => void;
};

export function SearchInput({
  debouncedTime = 500,
  queryKey = 'name',
  defaultValue,
  className,
  onRemoveValue,
  paramsToExcludeFromUrl,
  onValueChange,
  onKeyDown,
  ...props
}: SearchInputProps) {
  const [sp, setSp] = useSearchParams();
  const queryParam = sp.get(queryKey) || '';

  // Prefer an explicit defaultValue (including '') over the URL — filter
  // panels use local search and must not inherit a stale query param.
  const [query, setQuery] = useState(
    defaultValue !== undefined ? String(defaultValue) : queryParam
  );

  const debouncedFn = useDebounceCallback((query) => {
    if (onValueChange) {
      onValueChange(query);
      return;
    }

    setSp(
      (prev) => {
        paramsToExcludeFromUrl?.forEach((param) => {
          prev.delete(param);
        });
        if (query) {
          prev.set(queryKey, query);
        } else {
          prev.delete(queryKey);
        }
        return prev;
      },
      { replace: true }
    );
  }, debouncedTime);

  const removeSearchQuery = () => {
    if (onRemoveValue) {
      onRemoveValue();
      return;
    }

    setSp(
      (prev) => {
        prev.delete(queryKey);
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <Input
      placeholder='Write to search...'
      value={query}
      suffexIcon={
        <CircleXIcon
          onClick={() => {
            setQuery('');
            removeSearchQuery();
          }}
          aria-label='Clear filter'
          size={16}
          className={cn(
            'text-gray-900 invisible opacity-0 pointer-events-none',
            query &&
              'text-gray-900 visible opacity-80 hover:opacity-100 pointer-events-auto cursor-pointer'
          )}
          aria-hidden='true'
        />
      }
      onChange={(e) => {
        setQuery(e.target.value);
        if (!e.target.value) {
          removeSearchQuery();
        }
        debouncedFn(e.target.value || null);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        onKeyDown?.(e);
      }}
      size={'sm'}
      prefixIcon={<SearchIcon className='size-5 shrink-0' />}
      className={cn('z-5', className)}
      {...props}
    />
  );
}
